/**
 * RequestLogTable — the request-log rows, shared by the team-wide dashboard
 * band and the per-model Requests tab.
 *
 * One component so the two can't drift into showing different columns for
 * the same audit-log row — the per-model tab used to show four columns where
 * the dashboard shows nine, which is how a token split can be present on one
 * screen and missing on the other.
 *
 * The Model column is the only difference, and it is a prop: naming the model
 * on a page that IS one model is noise, while on the team-wide log it is the
 * whole point. Model cells link to `/models/:id` for team models only,
 * matched on the canonical `<teamSlug>/<slug>` key rather than a trailing
 * segment — `acme/glm-5.2` and `fireworks/glm-5.2` differ only in the prefix.
 *
 * A row whose body the gateway saved (it has a `trace_key`) opens the
 * request-detail sheet through `onOpenTrace` (TRA-1412): the whole row is
 * the click target and is keyboard-focusable. A row without one is inert.
 *
 * The client headers the gateway recorded get one column each (TRA-1397):
 * tenant, environment, traffic, session and initiator. Time, Model and
 * Trace sort the list through `onSort`; the rest are plain headers. The
 * fifteen columns are wider than the page on most screens, so the table
 * scrolls sideways inside its box with the Trajectories page's arrows.
 */

import { useRef } from 'react'
import { Link } from 'react-router-dom'

import type { RequestLogItem } from '@/api/generated/model'
import type { SortState } from '@/components/listing/listingParams'
import { SortHeader } from '@/components/listing/SortHeader'
import { ClientMetaCell } from '@/components/listing/ClientMetaCell'
import { CLIENT_META_COLUMNS } from '@/components/listing/clientMetadata'
import { type RequestSortField } from '@/components/models/home/requestLogParams'
import { SideScrollArrows } from '@/components/trajectories/SideScrollArrows'
import {
  formatTime,
  rowOpenProps,
  statusVariant,
  traceIdFromKey,
} from '@/components/models/home/requestLogRow'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

/** Null tokens are "the upstream reported none", which is not zero. */
function formatCount(value: number | null | undefined): string {
  return value == null ? '—' : value.toLocaleString()
}

/** The model cell: a link for the team's own models, plain text otherwise. */
function ModelCell({
  requestModel,
  modelId,
}: {
  requestModel: string | null | undefined
  modelId: string | undefined
}): React.JSX.Element {
  if (requestModel == null) {
    return <span className="text-muted-foreground">—</span>
  }
  if (modelId === undefined) {
    // A slug with no platform Model row — a catalog model, or an alias since
    // deleted. Named, but there is nothing to link to.
    return <span className="text-muted-foreground">{requestModel}</span>
  }
  return (
    <Link to={`/models/${modelId}`} className="text-foreground hover:underline">
      {requestModel}
    </Link>
  )
}

/** The trace cell: the captured trace's id, an em dash when no body was kept.
    Styled as a link when the row opens the detail sheet, since the id is
    the thing the reader clicks on. */
function TraceCell({
  traceKey,
  opens,
}: {
  traceKey: string | null | undefined
  opens: boolean
}): React.JSX.Element {
  if (!traceKey) return <span className="text-xs text-muted-foreground">—</span>
  return (
    <span
      className={
        opens
          ? 'font-mono text-xs text-foreground underline decoration-border underline-offset-2'
          : 'font-mono text-xs text-muted-foreground'
      }
    >
      {traceIdFromKey(traceKey)}
    </span>
  )
}

function RequestLogRow({
  item,
  showModel,
  modelId,
  onOpenTrace,
}: {
  item: RequestLogItem
  showModel: boolean
  modelId: string | undefined
  onOpenTrace: ((traceKey: string) => void) | undefined
}): React.JSX.Element {
  const openProps = rowOpenProps(item.trace_key, onOpenTrace, 'Open call details')
  return (
    <TableRow
      {...openProps}
      className={
        openProps === undefined
          ? undefined
          : 'cursor-pointer hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
      }
    >
      <TableCell className="whitespace-nowrap text-sm">
        {formatTime(item.timestamp)}
      </TableCell>
      <TableCell>
        <Badge variant={statusVariant(item.status_code)} className="font-mono">
          {item.status_code}
        </Badge>
        {item.error_message ? (
          <span className="ml-2 text-xs text-destructive" title={item.error_message}>
            error
          </span>
        ) : null}
      </TableCell>
      {showModel ? (
        <TableCell className="whitespace-nowrap font-mono text-xs">
          <ModelCell requestModel={item.request_model} modelId={modelId} />
        </TableCell>
      ) : null}
      <TableCell className="hidden font-mono text-xs text-muted-foreground lg:table-cell">
        {item.upstream_id ?? '—'}
        {item.fallback_from ? (
          // Served by a fallback: name the primary that failed and, when the
          // gateway recorded it, the literal model string that went out.
          <span
            className="ml-2 uppercase tracking-wider text-warning"
            title={`Primary '${item.fallback_from}' failed${
              item.served_model ? `; served as '${item.served_model}'` : ''
            }`}
          >
            fallback
          </span>
        ) : null}
      </TableCell>
      {CLIENT_META_COLUMNS.map((column) => (
        <ClientMetaCell key={column.label} value={column.value(item)} />
      ))}
      <TableCell className="text-right text-sm tabular-nums">
        {formatCount(item.prompt_tokens)}
      </TableCell>
      <TableCell className="text-right text-sm tabular-nums">
        {formatCount(item.cached_prompt_tokens)}
      </TableCell>
      <TableCell className="text-right text-sm tabular-nums">
        {formatCount(item.completion_tokens)}
      </TableCell>
      <TableCell className="text-right text-sm tabular-nums">
        {formatCount(item.total_tokens)}
      </TableCell>
      <TableCell className="whitespace-nowrap text-right text-sm tabular-nums">
        {item.latency_ms.toLocaleString()} ms
      </TableCell>
      <TableCell className="whitespace-nowrap">
        <TraceCell traceKey={item.trace_key} opens={openProps !== undefined} />
      </TableCell>
    </TableRow>
  )
}

interface RequestLogTableProps {
  items: RequestLogItem[]
  /** Team-wide log only: name the model on every row, and let it sort. */
  showModel?: boolean
  /** Canonical `<teamSlug>/<slug>` → model id, for linking team models. */
  teamModelIds?: Map<string, string>
  /** Open the detail sheet for a row's saved trace; omit for an inert log. */
  onOpenTrace?: (traceKey: string) => void
  sort: SortState<RequestSortField>
  onSort: (field: RequestSortField) => void
}

export function RequestLogTable({
  items,
  showModel = false,
  teamModelIds,
  onOpenTrace,
  sort,
  onSort,
}: RequestLogTableProps): React.JSX.Element {
  const scrollRef = useRef<HTMLDivElement>(null)
  return (
    // `relative` anchors the floating scroll arrows to this box.
    <div className="relative border border-border">
      <SideScrollArrows scrollRef={scrollRef} />
      <Table containerRef={scrollRef}>
        <TableHeader>
          <TableRow>
            <SortHeader field="last" label="Time" sort={sort} onSort={onSort} />
            <TableHead>Status</TableHead>
            {showModel ? (
              <SortHeader field="model" label="Model" sort={sort} onSort={onSort} />
            ) : null}
            <TableHead className="hidden lg:table-cell">Upstream</TableHead>
            {CLIENT_META_COLUMNS.map((column) => (
              <TableHead key={column.label} className="whitespace-nowrap">
                {column.label}
              </TableHead>
            ))}
            <TableHead className="text-right">Prompt</TableHead>
            <TableHead className="text-right">Cached</TableHead>
            <TableHead className="text-right">Completion</TableHead>
            <TableHead className="text-right">Total</TableHead>
            <TableHead className="text-right">Latency</TableHead>
            <SortHeader field="run" label="Trace" sort={sort} onSort={onSort} />
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => (
            <RequestLogRow
              key={item.id}
              item={item}
              showModel={showModel}
              onOpenTrace={onOpenTrace}
              modelId={
                item.request_model == null
                  ? undefined
                  : teamModelIds?.get(item.request_model)
              }
            />
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
